import {useState, useCallback, useEffect, useRef} from 'react';
import css from './ImageCarousel.module.scss';
import classNames from 'classnames';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faChevronLeft, faChevronRight, faXmark, faExpand} from '@fortawesome/free-solid-svg-icons';

export default function ImageCarousel({images, basePath}) {
  const [current, setCurrent] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const trackRef = useRef(null);

  const total = images.length;

  const prev = useCallback(() => {
    setCurrent(i => (i - 1 + total) % total);
  }, [total]);

  const next = useCallback(() => {
    setCurrent(i => (i + 1) % total);
  }, [total]);

  const goTo = useCallback((index) => {
    setCurrent(index);
  }, []);

  const openLightbox = useCallback(() => {
    setLightbox(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeLightbox = useCallback(() => {
    setLightbox(false);
    document.body.style.overflow = '';
  }, []);

  useEffect(() => {
    if (!lightbox) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightbox, closeLightbox, prev, next]);

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) next();
      else prev();
    }
    setTouchStart(null);
  };

  const resolveUrl = (src) => {
    if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('/')) {
      return src;
    }
    return `${basePath}/${src}`;
  };

  if (total === 0) return null;

  return (
    <>
      <div className={css.carousel}>
        <div
          className={css.viewport}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div
            ref={trackRef}
            className={css.track}
            style={{transform: `translateX(-${current * 100}%)`}}
          >
            {images.map((img, i) => (
              <div key={i} className={css.slide}>
                <img
                  src={resolveUrl(img.src)}
                  alt={img.alt || `Image ${i + 1}`}
                  loading="lazy"
                  className={css.image}
                  onClick={openLightbox}
                />
              </div>
            ))}
          </div>

          {total > 1 && (
            <>
              <button className={classNames(css.arrow, css.arrowLeft)} onClick={prev} aria-label="Previous">
                <FontAwesomeIcon icon={faChevronLeft}/>
              </button>
              <button className={classNames(css.arrow, css.arrowRight)} onClick={next} aria-label="Next">
                <FontAwesomeIcon icon={faChevronRight}/>
              </button>
            </>
          )}

          <button className={css.expandBtn} onClick={openLightbox} aria-label="Fullscreen">
            <FontAwesomeIcon icon={faExpand}/>
          </button>
        </div>

        {total > 1 && (
          <div className={css.dots}>
            {images.map((_, i) => (
              <button
                key={i}
                className={classNames(css.dot, {[css.dotActive]: i === current})}
                onClick={() => goTo(i)}
                aria-label={`Go to image ${i + 1}`}
              />
            ))}
          </div>
        )}
        
        {total > 1 && (
          <div className={css.counter}>{current + 1} / {total}</div>
        )}
      </div>

      {lightbox && (
        <div className={css.lightbox} onClick={closeLightbox}>
          <div className={css.lightboxContent} onClick={(e) => e.stopPropagation()}>
            <img
              src={resolveUrl(images[current].src)}
              alt={images[current].alt || ''}
              className={css.lightboxImage}
            />
            {images[current].alt && (
              <div className={css.lightboxCaption}>{images[current].alt}</div>
            )}
          </div>

          <button className={css.lightboxClose} onClick={closeLightbox} aria-label="Close">
            <FontAwesomeIcon icon={faXmark}/>
          </button>

          {total > 1 && (
            <>
              <button
                className={classNames(css.lightboxArrow, css.lightboxArrowLeft)}
                onClick={(e) => { e.stopPropagation(); prev(); }}
                aria-label="Previous"
              >
                <FontAwesomeIcon icon={faChevronLeft}/>
              </button>
              <button
                className={classNames(css.lightboxArrow, css.lightboxArrowRight)}
                onClick={(e) => { e.stopPropagation(); next(); }}
                aria-label="Next"
              >
                <FontAwesomeIcon icon={faChevronRight}/>
              </button>
            </>
          )}

          {total > 1 && (
            <div className={css.lightboxDots}>
              {images.map((_, i) => (
                <button
                  key={i}
                  className={classNames(css.dot, {[css.dotActive]: i === current})}
                  onClick={(e) => { e.stopPropagation(); goTo(i); }}
                  aria-label={`Go to image ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
